import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { initScene } from "../js/initScene";
import { loadModel } from "../js/loadModel";
import * as THREE from "three";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { getLoadingManager, subscribeLoading, reportXHRProgress, hideAfterFirstFrame } from "../utils/loadingManager";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { createPortal } from 'react-dom';
import { X, Search, RotateCcw, ImageOff } from 'lucide-react';
import { useQuote } from "../pricing/useQuote.js";
import QuoteWidget from "../components/QuoteWidget.jsx";
import { fetchQuote } from "../utils/fetchQuote.js";
import { dbg, table, DBG_ON } from "../utils/dbg";
import PricingDebug from "../components/PricingDebug.jsx";
import { useFxMap } from '../hooks/useFxMap';



const Viewer = ({ headerHeight = 60, modelCode: modelCodeFromProps }) => {
  const { modelCode: modelCodeFromUrl } = useParams();
  const modelCode = modelCodeFromProps || modelCodeFromUrl;
  const containerRef = useRef();
  const viewerRef = useRef(); // Dodajte referencu za ceo Viewer

  // === Full screen state ===
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenBtnRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_BASE || 'https://modularobjects.com';
  const STATIC_BASE = process.env.REACT_APP_STATIC_BASE || '';

  // Dodajte hover efekat za sve kontrole
  const [isHoveringControls, setIsHoveringControls] = useState(false);

  const [sceneState, setSceneState] = useState(null);
  const [explodeVectors, setExplodeVectors] = useState(new Map());
  const [originalPositions, setOriginalPositions] = useState(new Map());
  const [clipPlane] = useState(new THREE.Plane(new THREE.Vector3(0, 0, -1), 2));
  const [backgroundOverridden, setBackgroundOverridden] = useState(false);
  const [shadowsEnabled, setShadowsEnabled] = useState(false);
  const [wireframeLines, setWireframeLines] = useState([]);
  const [currentViewMode, setCurrentViewMode] = useState("realistic");

  const defaultBg =
  document.documentElement.classList.contains('dark') ? '#1f1f1f' : '#ffffff';

  // Nova stanja za selekciju materijala
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [zoomUrl, setZoomUrl] = useState("");
  const [materialOptions, setMaterialOptions] = useState([]);
  const [elementOptions, setElementOptions] = useState([]);
  const [showMaterialSelector, setShowMaterialSelector] = useState(false);
  const [materialPreview, setMaterialPreview] = useState("");
  const [materialList, setMaterialList] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [currentMatchingMaterials, setCurrentMatchingMaterials] = useState([]);
  const [appliedDecorMap, setAppliedDecorMap] = useState(new Map());
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const lastThicknessRef = useRef(null);

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  const { t } = useTranslation();

  // Controls launcher & modal state
const [controlsOpen, setControlsOpen] = useState(false);
const [controlsSection, setControlsSection] = useState('light'); // 'light' | 'look' | 'tools'

const openControls = (section) => {
  setControlsSection(section);
  setControlsOpen(true);
};
const closeControls = () => setControlsOpen(false);


  // === AR state ===
  const [arSupported, setArSupported] = useState(false);
  const [arActive, setArActive] = useState(false);
  const [placeOn, setPlaceOn] = useState("floor");
  const arOverlayRef = useRef(null);
  const startARRef = useRef(null);
  const bodyScrollBackupRef = useRef({ overflow:'', touchAction:'', overscrollBehavior:'' });

  // auto-rotacija preko ref-a da bismo je kontrolisali iz event handlera
const autoRotateRef = useRef(true);       // podrazumevano uključeno (promeni po želji)
const prevAutoRotateRef = useRef(true);   // da zapamtimo stanje pre AR-a

const placedInitiallyRef = useRef(false);
const initialDistanceRef = useRef(1.6); // metara – promeni na 2 po želji

const [azimuthDeg,  setAzimuthDeg]  = useState(45);
const [altitudeDeg, setAltitudeDeg] = useState(30);

// ADD: lagani preload keš
const preloadedByThickRef = useRef(new Map()); // Map<thickness:number, rows:any[]>
const preloadingRef = useRef(false);
const abortPreloadRef = useRef(null);

// ADD: helpers za idle i pauzu
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const requestIdle = (cb) => {
  const ric = window.requestIdleCallback;
  return ric ? ric(cb, { timeout: 1200 }) : setTimeout(() => cb({ timeRemaining: () => 10 }), 300);
};

function warmupImages(materials, limit = 24) {
  let count = 0;
  for (const m of materials) {
    if (count >= limit) break;
    const src = getMaterialImageSrc(m);
    if (src) {
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = src;
      count++;
    }
  }
}


// ADD: fino preuzimanje po debljinama, sa ograničenom konkurentnošću + abort
async function preloadMaterialsForThicknesses(thicknesses = []) {
  if (!thicknesses.length || preloadingRef.current) return;
  preloadingRef.current = true;

  const ctrl = new AbortController();
  abortPreloadRef.current = ctrl;

  const uniq = [...new Set(thicknesses.map(t => Math.round(Number(t))).filter(Number.isFinite))];

  let i = 0;
  const CONCURRENCY = 4;

  async function worker() {
    while (i < uniq.length && !ctrl.signal.aborted) {
      const t = uniq[i++];
      if (preloadedByThickRef.current.has(t)) continue;
      try {
        const r = await fetch(`${API_BASE}/api/materials/matching-materials?thickness=${t}`, {
          headers: { Accept: "application/json" }, signal: ctrl.signal
        });
        let data = (await r.json()) || [];
        if (!Array.isArray(data)) data = [];

        // (opciono) izračunaj cene odmah da grid ima brojke
        data = data.map(m => {
          const priceRSD = priceRsdWithMarkup(m);
          const priceEUR = round2(priceRSD / eurRate());
          return { ...m, __priceRSD: priceRSD, __priceEUR: priceEUR };
        });

        // ⬅️⬅️ KLJUČNO: hidracija slika za prvih 80, da grid ima thumbnails
        const top = data.slice(0, 80);
        const topHydrated = await hydrateImagesForAll(top);

        // zameni prvih 80 u “data” hidriranim verzijama
        const merged = [...topHydrated, ...data.slice(80)];

        preloadedByThickRef.current.set(t, merged);

        // malo prikoči da ne zatrpaš mrežu
        await sleep(120);
      } catch {
        preloadedByThickRef.current.set(t, []);
      }
    }
  }

  await Promise.race([
    Promise.all(Array.from({ length: Math.min(CONCURRENCY, uniq.length) }, worker)),
    sleep(2500),
  ]);

  preloadingRef.current = false;
}


// ADD: izvući verovatne debljine iz imena materijala u sceni (+ par podrazumevanih)
async function getLikelyThicknessesFromScene() {
  try {
    const names = sceneState?.modelGroup ? collectSceneMaterialNames(sceneState.modelGroup) : [];
    const vals = await Promise.all(names.map(n => resolveThickness(n).catch(() => NaN)));
    const rounded = vals.filter(Number.isFinite).map(v => Math.round(v));
    // obično potrebne “bucket” vrednosti
    [18, 38, 3, 4, 10, 12].forEach(t => { if (!rounded.includes(t)) rounded.push(t); });
    return [...new Set(rounded)];
  } catch {
    return [18, 38, 3, 4, 10, 12];
  }
}

// ADD: jedan entrypoint – koristi ga gde god treba da zagrejemo podatke
async function ensureWarmupPreload(optionalThicknesses) {
  try {
    const list = optionalThicknesses?.length ? optionalThicknesses : await getLikelyThicknessesFromScene();
    await preloadMaterialsForThicknesses(list);
    // opcionalno: zagrej i prve sličice za najčešću debljinu (npr. 18mm)
    const first = preloadedByThickRef.current.get(18) || preloadedByThickRef.current.get(list[0]) || [];
    warmupImages(first, 24);
  } catch {}
}

useEffect(() => {
  return () => { try { abortPreloadRef.current?.abort(); } catch {} };
}, []);

//useEffect(() => {
//  if (!sceneState) return;
//  const id = requestIdle(() => { ensureWarmupPreload(); });
//  return () => { if (typeof id === 'number') clearTimeout(id); };
//}, [sceneState]);


useEffect(() => {
  if (!sceneState) return;
  const { directionalLight, renderer, scene, camera } = sceneState;

  const az = THREE.MathUtils.degToRad(azimuthDeg);
  const al = THREE.MathUtils.degToRad(altitudeDeg);
  const r  = 10;

  directionalLight.position.set(
    r * Math.cos(al) * Math.sin(az),
    r * Math.sin(al),
    r * Math.cos(al) * Math.cos(az)
  );
  directionalLight.lookAt(0, 0, 0);
  renderer.render(scene, camera);
}, [sceneState, azimuthDeg, altitudeDeg]);

const homeRef = useRef(null);


  const restoreScrollAndLayout = () => {
  // 1) body/html
  const { overflow, touchAction, overscrollBehavior } = bodyScrollBackupRef.current || {};
  document.body.style.overflow = overflow ?? '';
  document.body.style.touchAction = touchAction ?? '';
  document.documentElement.style.overscrollBehavior = overscrollBehavior ?? '';
  document.body.classList.remove('overflow-hidden','modal-open');   // ako je nešto ostalo iz Tailwind/Modala
  document.documentElement.classList.remove('overflow-hidden');

  // 2) izađi iz DOM Fullscreen API ako je ostao
  try { if (document.fullscreenElement) document.exitFullscreen(); } catch {}

  // 3) izađi iz *custom* fullscreen-a (tvoj CSS fixed na vieweru)
  const el = viewerRef.current;
  if (el) {
    // ako je i dalje “lepljiv”
    const st = getComputedStyle(el);
    if (st.position === 'fixed') {
      // vrati originalne stilove (ako si ih čuvao) ili resetuj
      if (el.dataset.originalStyles) {
        Object.assign(el.style, JSON.parse(el.dataset.originalStyles));
      } else {
        el.style.position = '';
        el.style.top = '';
        el.style.left = '';
        el.style.width = '';
        el.style.height = '';
        el.style.zIndex = '';
      }
    }
  }
  if (containerRef.current) {
    containerRef.current.style.width  = '';
    containerRef.current.style.height = '';
  }
  // osveži state
  setIsFullscreen(false);

  // 4) mali nudge da vrati fokus i scroll
  requestAnimationFrame(() => window.scrollTo({ top: window.scrollY || 0 }));
};

  const lm = useMemo(() => getLoadingManager(), []);
 const bootTexLoader = useMemo(() => new THREE.TextureLoader(lm), [lm]);
 const rgbeLoader    = useMemo(() => new RGBELoader(lm), [lm]);
 const gltfLoader    = useMemo(() => new GLTFLoader(lm), [lm]);

 

 const [applying, setApplying] = useState(false);

 const [customColor, setCustomColor] = useState('#cfcfcf');

 const hexOk = (s) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(s || '').trim());

// === PIPETA / EYE DROPPER ===
const [pickingColor, setPickingColor] = useState(false);

const rgbaToHex = (r,g,b) => '#' + [r,g,b].map(v => v.toString(16).padStart(2,'0')).join('');
const wait2RAFs = () => new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

async function handlePickFromScene() {
  if (!selectedElement) {
    alert('Izaberi element sa leve strane.');
    return;
  }

  console.log('[PIPETTE] click');
  // 1) zatvori modal da vidiš scenu
  setShowMaterialSelector(false);
  setSelectedStructure(null);
  await wait2RAFs();

  // 2) Web API EyeDropper (Chrome/Edge + HTTPS/localhost)
  if ('EyeDropper' in window) {
    try {
      const eye = new window.EyeDropper();
      const { sRGBHex } = await eye.open();
      console.log('[PIPETTE] eyedropper picked:', sRGBHex);
      if (sRGBHex) setCustomColor(sRGBHex);
    } catch (e) {
      console.log('[PIPETTE] canceled/failed:', e?.name || e);
    } finally {
      setShowMaterialSelector(true); // vrati modal
    }
    return;
  }

  // 3) Fallback: jedan klik na WebGL canvas
  console.log('[PIPETTE] fallback mode ON');
  setPickingColor(true);
}

const hydratedOnceRef = useRef(false);

useEffect(() => {
  if (!showMaterialSelector) return;
  if (hydratedOnceRef.current) return;
  if (!materialOptions.length) return;

  const missing = materialOptions.some(m => !hasDecorImage(m) && m.DecorCode);
  if (!missing) return;

  let alive = true;
  (async () => {
    const hydrated = await hydrateImagesForAll(materialOptions);
    if (alive) {
      setMaterialOptions(hydrated);
      hydratedOnceRef.current = true;
    }
  })();

  return () => { alive = false; };
}, [showMaterialSelector]);


useEffect(() => {
  if (!pickingColor || !sceneState) return;

  const { renderer, scene, camera } = sceneState;
  const canvas = renderer.domElement;

  const onDown = async (ev) => {
    // očitaj 1 piksel ispod kursora
    const rect = canvas.getBoundingClientRect();
    const dpr  = window.devicePixelRatio || 1;
    const x = Math.floor((ev.clientX - rect.left) * dpr);
    const y = Math.floor((rect.bottom - ev.clientY) * dpr);

    const w = canvas.width, h = canvas.height;
    const rt = new THREE.WebGLRenderTarget(w, h, { depthBuffer: true });
    const px = new Uint8Array(4);

    renderer.setRenderTarget(rt);
    renderer.render(scene, camera);
    renderer.readRenderTargetPixels(rt, x, y, 1, 1, px);
    renderer.setRenderTarget(null);
    rt.dispose();

    const hex = rgbaToHex(px[0], px[1], px[2]);
    console.log('[PIPETTE] fallback picked:', hex);
    setCustomColor(hex);

    setPickingColor(false);
    setShowMaterialSelector(true);
  };

  const onEsc = (e) => {
    if (e.key === 'Escape') {
      console.log('[PIPETTE] canceled by ESC');
      setPickingColor(false);
      setShowMaterialSelector(true);
    }
  };

  canvas.style.cursor = 'crosshair';
  canvas.addEventListener('pointerdown', onDown, { once: true });
  window.addEventListener('keydown', onEsc);

  return () => {
    canvas.style.cursor = '';
    canvas.removeEventListener('pointerdown', onDown);
    window.removeEventListener('keydown', onEsc);
  };
}, [pickingColor, sceneState]);




 function applySolidColorToMaterial(materialName, hex) {
  if (!sceneState || !materialName || !hexOk(hex)) return;

  const { modelGroup, renderer, scene, camera } = sceneState;
  let count = 0;

  modelGroup.traverse((child) => {
    if (child.isMesh && child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat) => {
        if (mat.name === materialName) {
          mat.map = null;                 // skini teksturu
          mat.color?.set(hex);            // postavi punu boju
          if ("roughness" in mat) mat.roughness = 0.6;
          if ("metalness" in mat) mat.metalness = 0.0;
          mat.needsUpdate = true;
          count++;
        }
      });
    }
  });

  // zapamti izbor u tvojoj mapi
  const next = new Map(appliedDecorMap);
  next.set(materialName, { type: "color", hex });
  setAppliedDecorMap(next);

  console.log(`🎨 ${t("decor.apply_color")} ${hex} na "${materialName}" (ažurirano ${count}).`);
  renderer.render(scene, camera);
}

const applyCustomColor = () => {
  if (!selectedElement) return alert('Izaberi element sa leve strane.');
  if (!hexOk(customColor)) return alert('Upiši važeći HEX (npr. #e6e6e6).');
  applySolidColorToMaterial(selectedElement, customColor);
  setShowMaterialSelector(false);
  setSelectedStructure(null);
};

const PRICE_MULTIPLIER = 1.2;

const priceRsdWithMarkup = (pick) => {
  const baseRsd = parseRsdPrice(pick); 
  return Math.round((baseRsd * PRICE_MULTIPLIER + Number.EPSILON) * 100) / 100;
};


const parseRsd = (v) => {
  const n = parseFloat(String(v ?? 0).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;






 // Minimalni prazan quote da UI radi odmah
const EMPTY_QUOTE = {
  code: "",
  currency: "RSD",
  euro_rate: 117.0,
  discounts: { materials_pct: 0.1, equipment_pct: 0.15, services_pct: 0.2 },
  materials: { items: [], subtotal: 0, discount_pct: 0, discount_value: 0, total: 0 },
  equipment: { items: [], subtotal: 0, discount_pct: 0, discount_value: 0, total: 0 },
  services:  { items: [], subtotal: 0, discount_pct: 0, discount_value: 0, total: 0 },
  grand_subtotal: 0,
  grand_discount: 0,
  grand_total: 0
};




const quoteApi = useQuote(EMPTY_QUOTE);
// ako koristiš i18n:

const { i18n } = useTranslation();
const { fx, provider, defaultCurrency } = useFxMap({
  ttlMinutes: 360,
  initialLocaleCurrency: i18n?.language // npr. 'sr', 'en-US', 'pt-BR', ...
});

// kad fx stigne – setuj RSD kurs i inicijalnu valutu
useEffect(() => {
  const rsd = Number(fx?.rates?.RSD);
  if (rsd && quoteApi?.setEuroRate) quoteApi.setEuroRate(rsd);

  if (defaultCurrency && quoteApi?.setCurrency) {
    // setuj samo jednom ako nisi već ručno prebacio
    if (!quoteApi.currency || quoteApi.currency === 'RSD') {
      quoteApi.setCurrency(defaultCurrency);
    }
  }

  if (quoteApi?.setQuote) {
    quoteApi.setQuote(q => ({ ...q, fx_provider: provider }));
  }
}, [fx, provider, defaultCurrency]);

// Kurs: RSD po 1 EUR (npr. 117.0)
const eurRate = () => Number(quoteApi?.quote?.euro_rate || fx?.rates?.RSD || 117.0);


const parseRsdPrice = (pick, fallbackRate) => {
  const rate = Number(quoteApi?.quote?.euro_rate) || Number(fallbackRate ?? 117.0);
  const raw = pick?.CurrentPrice ?? pick?.Price ?? pick?.price ?? 0;
  const n = parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n)) return 0;

  const cur = String(pick?.Currency || pick?.currency || '').toUpperCase();

  let rsd;
  if (cur === 'EUR') rsd = n * rate;
  else if (cur === 'RSD' || cur === '') rsd = (n < 100) ? n * rate : n;
  else rsd = n;

  return rsd;
};





// helper da ispeglamo brojeve / ključeve iz tvog JSON-a
const normalizeQuote = (q) => {
  const num = (v) =>
    typeof v === "string" ? parseFloat(v.replace(",", ".")) : Number(v || 0);

  const cur = String(q.currency || q.Currency || "").trim().toUpperCase();
  const euro = num(q.euro_rate ?? q.euroRate ?? 117.0);

  // ⬅️ SVE konvertujemo u RSD odmah (ako je izvor EUR ili prazan)
  const toRSD = (v) => {
    const n = num(v);
    if (!Number.isFinite(n)) return 0;
    // ako fajl dolazi u EUR (ili je prazno) -> množi kursom; ako je RSD -> ostavi
    return (cur === "EUR" || cur === "") ? n * euro : n;
  };

  const normItems = (arr = []) =>
    arr.map((r) => ({
      name: r.name || r.MaterialName || "",
      thickness_mm: num(r.thickness_mm ?? r.Thickness),
      thickness:     num(r.thickness_mm ?? r.Thickness),
      area_m2:       num(r.area_m2 ?? r.AreaM2 ?? r.TotalArea),
      edge_length_m: num(r.edge_length_m ?? r.EdgeLength ?? 0),

      // ⬇️ cene uvek u RSD
      price_per_m2:     toRSD(r.price_per_m2 ?? r.PricePerUnit ?? r.pricePerM2),
      edge_price_per_m: toRSD(r.edge_price_per_m ?? r.EdgePricePerM ?? 0),
      unit_price:       toRSD(r.unit_price ?? r.UnitPrice ?? r.net_unit_price),
      net_unit_price:   toRSD(r.net_unit_price ?? r.UnitPrice ?? r.unit_price),
      total_cost:       toRSD(r.total_cost ?? r.TotalCost),

      quantity:   num(r.quantity ?? r.Qty),
      DecorCode:  r.DecorCode ?? null,
    }));

  const m = q.materials || {};
  const e = q.equipment || {};
  const s = q.services  || {};

  const materials = {
    items:         normItems(m.items),
    subtotal:      toRSD(m.subtotal),
    discount_pct:  num(m.discount_pct),
    discount_value:toRSD(m.discount_value),
    total:         toRSD(m.total),
  };
  const equipment = {
    items:         normItems(e.items),
    subtotal:      toRSD(e.subtotal),
    discount_pct:  num(e.discount_pct),
    discount_value:toRSD(e.discount_value),
    total:         toRSD(e.total),
  };
  const services  = {
    items:         normItems(s.items),
    subtotal:      toRSD(s.subtotal),
    discount_pct:  num(s.discount_pct),
    discount_value:toRSD(s.discount_value),
    total:         toRSD(s.total),
  };

  const grand_subtotal = toRSD(q.grand_subtotal ?? (materials.subtotal + equipment.subtotal + services.subtotal));
  const grand_discount = toRSD(q.grand_discount ?? (materials.discount_value + equipment.discount_value + services.discount_value));
  const grand_total    = toRSD(q.grand_total    ?? (materials.total + equipment.total + services.total));

  const result = {
    code: q.code || q.Code || "",
    // Sačuvaj info o izvornoj valuti, ali interni base je RSD
    source_currency: cur || "EUR",
    currency: "RSD",
    euro_rate: euro,
    discounts: q.discounts || { materials_pct: 0, equipment_pct: 0, services_pct: 0 },
    materials,
    equipment,
    services,
    grand_subtotal,
    grand_discount,
    grand_total,
    grandSubtotal: grand_subtotal,
    grandDiscount: grand_discount,
    grandTotal:    grand_total,
  };

  dbg("normalizeQuote →", {
    code: result.code,
    euro_rate: result.euro_rate,
    source_currency: result.source_currency,
    currency_base: result.currency, // RSD
    materialsItems: result.materials.items.length,
    equipmentItems: result.equipment.items.length,
    servicesItems: result.services.items.length,
    grandTotals: {
      subtotal: result.grand_subtotal,
      discount: result.grand_discount,
      total: result.grand_total
    }
  });
  table("materials.items", result.materials.items);
  table("equipment.items", result.equipment.items);
  table("services.items",  result.services.items);

  return result;
};



const ASSETS_BASE = process.env.REACT_APP_STATIC_BASE || '';

function detectMimeFromB64(b64 = '') {
  const h = b64.slice(0, 12);
  if (h.startsWith('/9j/'))   return 'image/jpeg';
  if (h.startsWith('iVBOR'))  return 'image/png';
  if (h.startsWith('UklGR'))  return 'image/webp';
  if (h.startsWith('R0lGOD')) return 'image/gif';
  return 'image/jpeg';
}

// Vraća spreman <img src="..."> string za sve slučajeve (base64 i URL)
function getMaterialImageSrc(m) {
  if (!m) return null;

  // Base64 varijante
  const b64 =
    m.imageBase64 ?? m.ImageBase64 ??
    m.thumbnailBase64 ?? m.ThumbnailBase64 ?? null;

  if (typeof b64 === 'string' && b64.trim()) {
    const clean = b64.trim().replace(/\s+/g, '');
    if (clean.startsWith('data:image/')) return clean;
    return `data:${detectMimeFromB64(clean)};base64,${clean}`;
  }

  // URL varijante
  const rawUrl =
    m.imageUrl ?? m.ImageUrl ??
    m.thumbnailUrl ?? m.ThumbnailUrl ??
    m.textureUrl ?? m.TextureUrl ?? null;

  if (!rawUrl) return null;

  const s = String(rawUrl);
  if (s.startsWith('data:image/')) return s;
  if (/^https?:\/\//i.test(s) || s.startsWith('/')) return s;
  // relativne putanje – nakači STATIC_BASE
  return `${ASSETS_BASE.replace(/\/$/, '')}/${s.replace(/^\//, '')}`;
}

const hasDecorImage = (m) => !!getMaterialImageSrc(m);







useEffect(() => {
  if (!viewerRef.current) return;
  if (isFullscreen) {
    viewerRef.current.style.zIndex = showMaterialSelector ? '9990' : '9998';
  } else {
    viewerRef.current.style.zIndex = '1';
  }
}, [isFullscreen, showMaterialSelector]);


// Funkcije za CUSTOM full screen
  const enterFullscreen = () => {
    const elem = viewerRef.current; // Koristite ceo Viewer container
    if (!elem) return;

    // Sačuvaj originalne stilove
    const originalStyles = {
      position: elem.style.position,
      top: elem.style.top,
      left: elem.style.left,
      width: elem.style.width,
      height: elem.style.height,
      zIndex: elem.style.zIndex,
    };

    elem.dataset.originalStyles = JSON.stringify(originalStyles);

    // Postavi stilove za fullscreen
    elem.style.position = 'fixed';
    elem.style.top = '0';
    elem.style.left = '0';
    elem.style.width = '100vw';
    elem.style.height = '100vh';
    elem.style.zIndex = '9998'; // Visok zIndex

    // Postavi i Three.js container za fullscreen
    if (containerRef.current) {
      containerRef.current.style.width = '100%';
      containerRef.current.style.height = '100%';
    }

    setIsFullscreen(true);
    document.body.style.overflow = 'hidden'; // Spriječi scroll
  };

  const exitFullscreen = () => {
    const elem = viewerRef.current;
    if (!elem) return;

    // Vrati originalne stilove
    if (elem.dataset.originalStyles) {
      const originalStyles = JSON.parse(elem.dataset.originalStyles);
      Object.assign(elem.style, originalStyles);
    }

    // Vrati i Three.js container
    if (containerRef.current) {
      containerRef.current.style.width = '';
      containerRef.current.style.height = '';
    }

    setIsFullscreen(false);
    document.body.style.overflow = ''; // Vrati scroll
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

// ⚠️ Teksture koje korisnik menja posle (dekori) – BEZ manager-a:
const dynamicTexLoader = useMemo(() => new THREE.TextureLoader(), []);

  const handleBuyClick = async () => {
    try {
      const res = await fetch(
        "https://modularobjects.com/checkout/api/payment/purchase-model",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const text = await res.text();
      console.log("📦 Server response:", text);

      let sessionId = null;
      try {
        const data = JSON.parse(text);
        sessionId = data.id;
      } catch (err) {
        console.error("❌ JSON parse error:", err);
        alert("Greška: server nije vratio ispravan JSON.");
        return;
      }

      if (!sessionId) {
        alert("❌ Stripe session ID nije dobijen iz backend odgovora.");
        return;
      }

      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId });
    } catch (err) {
      console.error("❌ handleBuyClick greška:", err);
      alert("Došlo je do greške pri komunikaciji sa serverom.");
    }
  };

  // Funkcija za učitavanje cena materijala
const loadMaterialPrices = async (modelCode) => {
  try {
    const base = process.env.REACT_APP_STATIC_BASE || "";
    const candidates = [
      `${base}/demo/3dmodels/materials_${modelCode}.json`,
      `${base}/demo/3dmodels/${modelCode}/materials.json`,
    ];

    for (const url of candidates) {
      const res = await fetch(url, { cache: "no-store" }).catch(() => null);
      if (!res?.ok) continue;
      const txt = await res.text();
      let data = null;
      try { data = JSON.parse(txt); } catch { continue; }
      const total = data.reduce((s, r) => s + (Number(r.TotalCost) || 0), 0);
      setMaterialList(data);
      setTotalPrice(total);
      return data;
    }

    // ništa nije nađeno
    setMaterialList([]);
    setTotalPrice(0);
    return [];
  } catch {
    setMaterialList([]);
    setTotalPrice(0);
    return [];
  }
};


// helper – izvuci imena materijala iz bilo kog group-a
function collectSceneMaterialNames(group) {
  const names = new Set();
  group.traverse((o) => {
    if (o.isMesh && o.material) {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach(m => {
        const n = (m?.name || "").trim();
        if (n) names.add(n);
      });
    }
  });
  return [...names];
}


// Funkcija za popunjavanje dropdown-a sa postojećim materijalima
const populateExistingMaterialsDropdown = () => {
  if (!sceneState) return;
  
  const { modelGroup } = sceneState;
  const elements = [];
  
  modelGroup.traverse((child) => {
    if (child.isMesh && child.name) {
      elements.push(child.name);
    }
  });
  
  setElementOptions([...new Set(elements)]); // Ukloni duplikate
};

// Funkcija za promenu postojećeg materijala
// --- THICKNESS helpers ---
// keš da ne vučeš JSON svaki put
const materialsCacheRef = useRef(new Map()); // key: modelCode -> rows[]

const normalize = (s = "") =>
  s.toLowerCase()
   .normalize("NFD").replace(/\p{Diacritic}/gu, "") // skini dijakritike
   .replace(/\s+/g, " ")
   .trim();

const loadMaterialsForModel = async (code) => {
  if (!code) return [];
  const cache = materialsCacheRef.current;
  if (cache.has(code)) return cache.get(code);
  try {
    const resp = await fetch(`${STATIC_BASE}/demo/3dmodels/materials_${code}.json`, { cache: "no-store" });
    if (!resp.ok) throw new Error(`materials_${code}.json not found`);
    const rows = await resp.json();
    cache.set(code, Array.isArray(rows) ? rows : []);
    return cache.get(code);
  } catch (e) {
    console.warn("materials JSON error:", e);
    cache.set(code, []);
    return [];
  }
};

// 1) Probaj da nađeš debljinu u materials_<code>.json (tačno pa fuzzy)
const getThicknessFromJson = async (materialName) => {
  const rows = await loadMaterialsForModel(modelCode);
  if (!rows.length) return NaN;

  const target = normalize(materialName);

  // tačno poklapanje
  let hit = rows.find(r => normalize(r.MaterialName) === target);
  if (hit?.Thickness != null) return Number(hit.Thickness);

  // fuzzy: ako je naziv iz modela duži/kratak, probaj contains u oba smera
  const candidates = rows
    .map(r => ({ row: r, norm: normalize(r.MaterialName) }))
    .filter(x => x.norm.includes(target) || target.includes(x.norm));

  if (candidates.length) {
    // uzmi najbliži (najduži match)
    const best = candidates.sort((a,b) => b.norm.length - a.norm.length)[0].row;
    if (best?.Thickness != null) return Number(best.Thickness);
  }

  return NaN;
};

// 2) Fallback: izvuci iz naziva (radi i sa 38,6mm)
const getThicknessFromName = (name = "") => {
  const s = name
    .toLowerCase()
    .normalize("NFD").replace(/\p{Diacritic}/gu, "")
    .replace(/,/g, "."); // 38,6 -> 38.6

  // uhvati sve "xx mm" ili "xxmm" (podržava i ćir. мм)
  const re = /(\d+(?:\.\d+)?)\s*[mм]{2}\b/g;
  const candidates = [...s.matchAll(re)]
    .map(m => parseFloat(m[1]))
    .filter(n => Number.isFinite(n) && n > 0 && n < 100);

  if (candidates.length) {
    // ako ima više, uzmi najveću (npr. 38.6 umesto 2)
    return candidates.sort((a,b) => b - a)[0];
  }

  // heuristike
  if (s.includes("radna plo") || s.includes("worktop") || s.includes("counter")) return 38;
  if (s.includes("back") || s.includes("ledj") || s.includes("leđ"))             return 3;
  if (s.includes("glass") || s.includes("stakl"))                                return 4;
  if (s.includes("profil")|| s.includes("alumin"))                               return 2;

  return 18; // default
};

// 3) Kombinovani “resolver”
const resolveThickness = async (materialName) => {
  let th = await getThicknessFromJson(materialName);       // prvo JSON
  //if (!Number.isFinite(th)) th = getThicknessFromName(materialName); // pa naziv
  //if (!Number.isFinite(th)) th = 18;
  return th;
};

// helperi na vrhu file-a (ili iznad onExistingMaterialChange)
const parseThickness = (m) => {
  const raw = m?.Thickness ?? m?.thickness_mm ?? m?.thickness;
  const n = parseFloat(String(raw ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

// tolerancija po "grupama" (HDF 3–4 mm, ploča ~18 mm, radna ploča ~38 mm)
const sameThickness = (tItem, tTarget, tol = 0.9) => {
  if (!Number.isFinite(tTarget)) return true;
  if (!Number.isFinite(tItem))   return false;

  // Za tanke (3–4 mm) grupišemo ≤4.5 mm
  if (tTarget <= 4.5) return tItem <= 4.5;

  // Za radne ploče (30+ mm) dozvoli malo širu toleranciju
  if (tTarget >= 30) return Math.abs(tItem - tTarget) <= 1.5;

  // Standardne ploče (16–22 mm): ±0.9 mm
  return Math.abs(tItem - tTarget) <= tol;
};



const fetchDecorImage = async (decorCode) => {
  try {
    const res = await fetch(`${API_BASE}/api/decor?decorCode=${encodeURIComponent(decorCode)}`, {
      headers: { Accept: "application/json" }
    });
    const txt = await res.text();
    const j = JSON.parse(txt);
    const img = j.imageBase64 || j.ImageBase64;
    return (typeof img === "string" && img.trim()) ? img : null;
  } catch {
    return null;
  }
};

// negde na vrhu fajla (ili iznad onExistingMaterialChange)
const minEurForThickness = (t) => {
  if (!Number.isFinite(t)) return 14; // default
  if (t <= 4)  return 5;  // HDF/lesonit 3–4mm – ne seci jeftine
  if (t <= 10) return 8;  // 8–10mm – blaži prag
  return 14;              // 12–19mm+ – standardni prag
};


const onExistingMaterialChange = async (materialName) => {
  setSelectedElement(materialName || "");
  setSelectedMaterial("");
  setSelectedStructure(null);
  setMaterialPreview("");
  setMaterialOptions([]);
  if (!materialName) return;

  setLoadingMaterials(true);
  try {
    const thicknessReal = await resolveThickness(materialName);
    const apiThickness  = Math.round(thicknessReal);
    lastThicknessRef.current = thicknessReal;

    // === ADD: probaj iz keša za tu debljinu ===
    let materials = [];
    const cached = preloadedByThickRef.current.get(apiThickness);
    if (Array.isArray(cached) && cached.length) {
      materials = cached.map(m => ({ ...m })); // kopija
    } else {
      // tvoj postojeći fetch (fallback)
      const url = `${API_BASE}/api/materials/matching-materials?thickness=${encodeURIComponent(apiThickness)}`;
      const r = await fetch(url, { headers: { Accept: "application/json" } });
      const txt = await r.text();
      try { materials = JSON.parse(txt) || []; } catch { materials = []; }
      // keširaj za sledeći put
      preloadedByThickRef.current.set(apiThickness, Array.isArray(materials) ? materials : []);
    }

    // 3) fallback na sve, pa ±2mm
    if (materials.length === 0) {
      try {
        const r2 = await fetch(`${API_BASE}/api/materials/matching-materials`, { headers: { Accept: "application/json" }});
        const txt2 = await r2.text();
        const all = JSON.parse(txt2);
        const near = Array.isArray(all)
          ? all.filter(m => {
              const t = parseFloat(m.Thickness);
              return Number.isFinite(t) && Math.abs(t - apiThickness) <= 2;
            })
          : [];
        materials = near.length ? near : (Array.isArray(all) ? all : []);
      } catch (e2) {
        console.error("Fallback fetch error:", e2);
        materials = [];
      }
    }

    //    SADA: pre nego što kreneš na mapiranje cena, isfiltriraj po debljini
const strictTol = 9; // za 18 mm ploče npr. 17.9–18.9
let filtered = materials.filter(m => sameThickness(parseThickness(m), thicknessReal, strictTol));

// ako je filter previše strog i ostane prazno, raširi toleranciju (npr. ±2 mm)
if (filtered.length === 0) {
  filtered = materials.filter(m => {
    const th = parseThickness(m);
    return Number.isFinite(th) && Math.abs(th - thicknessReal) <= 2;
  });
}

// zameni materials filtriranom listom
materials = filtered;

   // 4) normalizacija + cene
const minEur = minEurForThickness(apiThickness);

materials = materials
  .map(m => {
    const priceRSD = priceRsdWithMarkup(m);
    const priceEUR = round2(priceRSD / eurRate());
    return { ...m, __priceRSD: priceRSD, __priceEUR: priceEUR };
  })
  .filter(m => (m.__priceEUR ?? 0) >= minEur)
  .sort((a, b) => (a.__priceEUR || Infinity) - (b.__priceEUR || Infinity));

// 5) tek sad hidriraš slike (manje podataka)
materials = await hydrateImagesForAll(materials);

    // 6) upiši u state
    setMaterialOptions(materials);

    // 7) pokušaј auto-prepoznavanja već primenjenog dekora
    let chosen = appliedDecorMap.get(materialName) || null;

    if (!chosen && sceneState?.modelGroup && materials.length) {
      let currentSrc = null;
      sceneState.modelGroup.traverse(child => {
        if (child.isMesh) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach(mat => {
            if (!currentSrc && mat.name === materialName && mat.map?.image?.src) {
              currentSrc = mat.map.image.src;
            }
          });
        }
      });

      if (currentSrc) {
        for (const m of materials) {
          // Ako koristiš helper:
          const b64 = m.imageBase64 || m.ImageBase64;
const src = b64 ? `data:image/jpeg;base64,${b64}` : null;

          // Ako helper vrati data: URL lako je uporediti početak
          if (src && src.startsWith('data:image/') && currentSrc.startsWith(src.slice(0, 100))) {
            chosen = m.DecorCode;
            break;
          }

          // (opciono) ako je u sceni URL, a iz baze base64, ovo neće poklopiti — to je OK.
        }
      }
    }

    if (chosen) setSelectedMaterial(chosen);

  } catch (err) {
    console.error("❌ matching-materials fetch error:", err);
    setMaterialOptions([]);
  } finally {
    setLoadingMaterials(false);
  }
};




// Funkcija za primenu materijala
const applyMaterial = async () => {
  if (!sceneState || !selectedElement || !selectedMaterial) return;

  try {
    const res = await fetch(
      `${API_BASE}/api/decor?decorCode=${encodeURIComponent(selectedMaterial)}`,
      { headers: { Accept: 'application/json' } }
    );
    if (!res.ok) throw new Error(`decor API error: ${res.status}`);

    // bezbedno parsiranje (nekad server vrati HTML, pa prvo uzmi text)
    const text = await res.text();
    let decorData;
    try { decorData = JSON.parse(text); }
    catch { throw new Error('Decor odgovor nije JSON'); }

    const img64 = decorData.imageBase64 || decorData.ImageBase64;
    if (!img64) throw new Error('Nema imageBase64 u odgovoru');

    const url = `data:image/jpeg;base64,${img64}`;

    // pretvori TextureLoader u promise da uhvatiš greške
    const texture = await new Promise((resolve, reject) => {
      new THREE.TextureLoader().load(url, (t) => {
        t.colorSpace = THREE.SRGBColorSpace;
        t.wrapS = THREE.MirroredRepeatWrapping;
        t.wrapT = THREE.MirroredRepeatWrapping;
        t.repeat.set(1, 1);
        resolve(t);
      }, undefined, reject);
    });

    // primeni na sve materijale sa tim imenom
    updateMaterialsByName(selectedElement, texture);

    // zapamti izbor
    const next = new Map(appliedDecorMap);
    next.set(selectedElement, selectedMaterial);
    setAppliedDecorMap(next);

    const pick = materialOptions.find(m => m.DecorCode === selectedMaterial);

// ✅ uvećana cena u RSD koju šaljemo engine-u
const priceRSD = typeof pick?.__priceRSD === 'number'
   ? pick.__priceRSD
   : priceRsdWithMarkup(pick);

const guessedThickness = Number.isFinite(lastThicknessRef.current) ? lastThicknessRef.current : null;

quoteApi.applyMaterialPriceFromViewer(
  selectedElement,
  guessedThickness,
  priceRSD
);



    // kartica sa skorijim izborima
    const match = materialOptions.find(m => m.DecorCode === selectedMaterial);
    if (match) {
      setSelectedMaterials(prev => {
        const arr = [...prev];
        if (arr.length >= 10) arr.shift();
        arr.push({ name: selectedElement, text: match.DistributorName || 'Nepoznat', imageUrl: url });
        return arr;
      });
    }

    sceneState.renderer.render(sceneState.scene, sceneState.camera);
     return true;  // ✅ signal uspeha
  } catch (err) {
    console.error('applyMaterial error:', err);
    return false; // ❌ signal neuspeha
  }
};



// Funkcija za ažuriranje materijala po imenu
const updateMaterialsByName = (materialName, texture) => {
  if (!sceneState) return;
  
  const { modelGroup } = sceneState;
  let updatedCount = 0;
  
  modelGroup.traverse((child) => {
    if (child.isMesh && child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach(mat => {
          if (mat.name === materialName) {
            mat.map = texture;
            mat.color.set(0xffffff);
            mat.needsUpdate = true;
            updatedCount++;
          }
        });
      } else {
        if (child.material.name === materialName) {
          child.material.map = texture;
          child.material.color.set(0xffffff);
          child.material.needsUpdate = true;
          updatedCount++;
        }
      }
    }
  });
  
  console.log("Ukupno ažuriranih materijala:", updatedCount);
};

// Funkcija za ažuriranje cene
const updateFrontMaterialPrice = (materialName, decorCode) => {
  const pick = materialOptions.find(m => m.DecorCode === decorCode);
  if (!pick) return;
  const pricePerUnit = priceRsdWithMarkup(pick); // uvek RSD ×1.2

  setMaterialList(prev => {
    // probaj direktan match po imenu
    let idx = prev.findIndex(m => (m.MaterialName || "").toLowerCase() === materialName.toLowerCase());
    if (idx === -1) {
      // fallback: izaberi bucket po debljini
      const t = getThicknessFromName(materialName);
      idx = prev.findIndex(m => Math.round(m.Thickness) === Math.round(t));
    }
    if (idx === -1) return prev;

    const updated = [...prev];
    const row = updated[idx];
    const qtyOrArea =
   row.TotalVolume ??
   row.AreaM2 ??
   row.TotalArea ??
   row.Quantity ??
   row.Qty ?? 0;

 updated[idx] = {
   ...row,
   PricePerUnit: pricePerUnit,
   TotalCost: round2(qtyOrArea * pricePerUnit),
   DecorCode: decorCode
};
    // preračunaj total
    const newTotal = updated.reduce((s, r) => s + (Number(r.TotalCost) || 0), 0);
setTotalPrice(round2(newTotal));
    // (po želji) pošalji na backend
    fetch(`${API_BASE}/api/save-material-list`, {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify(updated)
    }).catch(console.error);

    return updated;
  });
};




// Funkcija za ekstrakciju imena materijala iz modela
const extractMaterialNames = () => {
  if (!sceneState) return;
  const { modelGroup } = sceneState;
  const names = new Set();

  modelGroup.traverse(child => {
    if (child.isMesh && child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(m => {
        // Proverite da li materijal ima ime i da nije prazan string
        if (m && m.name && m.name.trim() !== "") {
          names.add(m.name.trim());
        }
      });
    }
  });

  console.log("Ukupno pronadjenih materijala:", names.size);
if (names.size === 0) {
  console.warn("Nije pronadjen nijedan materijal sa imenom!");
  // Dodatna debug informacija
  sceneState.modelGroup.traverse(child => {
    if (child.isMesh) {
      console.log("Mesh:", child.name, "Material:", child.material);
    }
  });
}

  // Dodajte i imena mesh-ova kao fallback
  modelGroup.traverse(child => {
    if (child.isMesh && child.name && child.name.trim() !== "" && names.size === 0) {
      names.add(child.name.trim());
    }
  });

  console.log("Pronađena imena materijala:", Array.from(names));
  setElementOptions(Array.from(names));
};

  const toggleShadows = () => {
    if (!sceneState) return;
    
    const { renderer, directionalLight, scene } = sceneState;
    const newShadowsState = !shadowsEnabled;
    
    renderer.shadowMap.enabled = newShadowsState;
    directionalLight.castShadow = newShadowsState;
    
    scene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = newShadowsState;
        child.receiveShadow = newShadowsState;
      }
    });
    
    setShadowsEnabled(newShadowsState);
    renderer.render(scene, sceneState.camera);
  };

  const updateViewMode = (mode) => {
    if (!sceneState) return;
    
    const { modelGroup, scene, renderer, camera } = sceneState;
    const model = modelGroup;
    
    wireframeLines.forEach(line => {
      if (line.parent) line.parent.remove(line);
    });
    setWireframeLines([]);
    
    let newWireframeLines = [];

   


    model.traverse((child) => {
      if (child.isMesh) {
        child.material.wireframe = false;
        child.visible = true;

        if (mode === "wireframe") {
          child.visible = false;
          
          const edges = new THREE.EdgesGeometry(child.geometry);
          const edgeColor = child.material.color || new THREE.Color(0x000000);
          const lineMaterial = new THREE.LineBasicMaterial({ color: edgeColor });

          const wireframe = new THREE.LineSegments(edges, lineMaterial);

          child.parent.add(wireframe);
          wireframe.position.copy(child.position);
          wireframe.rotation.copy(child.rotation);
          wireframe.scale.copy(child.scale);

          newWireframeLines.push(wireframe);
        }

        if (mode === "gray") {
          child.visible = true;
          child.material.color.set(0x888888);
          child.material.wireframe = false;
        }

        if (mode === "realistic") {
          child.visible = true;
          child.material.wireframe = false;
          child.material.needsUpdate = true;
        }
        
        child.material.needsUpdate = true;
      }
    });

    setWireframeLines(newWireframeLines);
    setCurrentViewMode(mode);
    
    model.traverse((child) => {
      if (child.isMesh) {
        child.material.clippingPlanes = [clipPlane];
        child.material.clipIntersection = true;
        child.material.needsUpdate = true;
      }
    });
    
    renderer.render(scene, camera);
  };

  // Dodajte ove state varijable
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('price');
  const [filteredMaterials, setFilteredMaterials] = useState([]);


  // Funkcija za dobijanje URL-a slike
  const getImageUrl = (material) => {
    if (!hasDecorImage(material)) return null;
    
    const img = material.imageBase64 || material.ImageBase64 || 
                material.image || material.Image;
    
    // Ako već ima data URL, vrati ga
    if (img.startsWith('data:image/')) {
      return img;
    }
    
    // Inače kreiraj data URL od base64 stringa
    return `data:image/jpeg;base64,${img}`;
  };

  // Dodajte ovu funkciju za filtriranje duplikata
const filterUniqueDecorCodes = (materials) => {
  const seen = new Set();
  const unique = [];
  
  for (const material of materials) {
    const key = `${material.DecorCode}_${material.Structure || 'N/A'}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(material);
    }
  }
  
  return unique;
};

// Ažurirajte useEffect za filtriranje i sortiranje
useEffect(() => {
  if (!materialOptions.length) {
    setFilteredMaterials([]);
    return;
  }

  let filtered = [...materialOptions];
  
  // Filtriranje duplikata po DecorCode + Structure
  filtered = filterUniqueDecorCodes(filtered);
  
  // Filtriranje po pretrazi
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(material => 
      (material.DistributorName && material.DistributorName.toLowerCase().includes(query)) ||
      (material.DecorCode && material.DecorCode.toLowerCase().includes(query)) ||
      (material.Structure && material.Structure.toLowerCase().includes(query))
    );
  }
  
  // Sortiranje
  if (sortBy === 'price') {
    filtered.sort((a, b) => (a.__priceEUR || 0) - (b.__priceEUR || 0));
  } else if (sortBy === 'name') {
    filtered.sort((a, b) => (a.DistributorName || '').localeCompare(b.DistributorName || ''));
  } else if (sortBy === 'structure') {
    filtered.sort((a, b) => (a.Structure || '').localeCompare(b.Structure || ''));
  }
  
  setFilteredMaterials(filtered);
}, [materialOptions, searchQuery, sortBy]);

  // Funkcija za filtriranje i sortiranje materijala
  useEffect(() => {
    if (!materialOptions.length) {
      setFilteredMaterials([]);
      return;
    }

    let filtered = [...materialOptions];
    
    // Filtriranje po pretrazi
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(material => 
        (material.DistributorName && material.DistributorName.toLowerCase().includes(query)) ||
        (material.DecorCode && material.DecorCode.toLowerCase().includes(query))
      );
    }
    
    // Sortiranje
    if (sortBy === 'price') {
      filtered.sort((a, b) => (a.__priceEUR || 0) - (b.__priceEUR || 0));
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => (a.DistributorName || '').localeCompare(b.DistributorName || ''));
    }
    
    setFilteredMaterials(filtered);
  }, [materialOptions, searchQuery, sortBy]);

  

  useEffect(() => {
  if (modelCode) {
    loadMaterialPrices(modelCode);
  }
}, [modelCode]);

useEffect(() => {
  if (selectedMaterial && !materialOptions.some(m => m.DecorCode === selectedMaterial && hasDecorImage(m))) {
    setSelectedMaterial("");
    setMaterialPreview("");
  }
}, [materialOptions, selectedMaterial, sortBy]);


   
useEffect(() => {
  let alive = true;
  (async () => {
    const raw = await fetchQuote(modelCode);
    if (!alive) return;

    if (raw) {
      const nq = normalizeQuote(raw);           // <— obavezno
      // zovi JEDNU od metoda koje hook ima
      if (typeof quoteApi.replaceQuote === "function") quoteApi.replaceQuote(nq);
      else if (typeof quoteApi.replace === "function") quoteApi.replace(nq);
      else if (typeof quoteApi.setQuote === "function") quoteApi.setQuote(nq);
      else console.warn("useQuote nema setter (replaceQuote/replace/setQuote).");
      console.log("✅ Učitana ponuda iz", window.__lastQuoteUrl, nq);
    } else {
      console.warn("Nema offer/quote JSON-a za", modelCode);
    }
  })();
  return () => { alive = false; };
}, [modelCode]);



// Efekat za praćenje full screen promena
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || 
                       document.webkitFullscreenElement || 
                       document.msFullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

// U useEffect za sceneState
useEffect(() => {
  if (sceneState) {
    console.log("Scena je inicijalizovana, pokušavam da izvučem materijale...");
    extractMaterialNames();
  }
}, [sceneState]);

useEffect(() => {
  if (sceneState) {
    populateExistingMaterialsDropdown();
  }
}, [sceneState]);

  useEffect(() => {
    if (!containerRef.current) return;

    let renderer, controls;

    // za UI dugme "toggleRotation"
    let autoRotate = true;

    const setup = async () => {
      const {
        scene,
        camera,
        renderer: _renderer,
        controls: _controls,
        modelGroup,
        ambientLight,
        directionalLight,
      } = initScene(containerRef.current);

      ('/demo/3d/textures/hdr_environment.hdr', (tex) => {
  tex.mapping = THREE.EquirectangularReflectionMapping;
  scene.envirgbeLoader.loadronment = tex;
});

      // 🚀 Dodajte ove dve varijable za klackalicu
      let currentRotation = 0;
      let rotationSpeed = 0.002;
      const maxRotation = 0.5;

      _renderer.shadowMap.enabled = true;
      _renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 1024;
      directionalLight.shadow.mapSize.height = 1024;
      directionalLight.shadow.camera.near = 0.5;
      directionalLight.shadow.camera.far = 500;

      // Kreiraj glavni kontejner za sve objekte koji će se rotirati
      const mainContainer = new THREE.Group();
      scene.add(mainContainer);

      let gridHelper = new THREE.GridHelper(5, 10);
      gridHelper.name = "gridHelper";
      mainContainer.add(gridHelper); // Dodaj grid u glavni kontejner

      // Dodaj modelGroup u glavni kontejner umesto direktno u scenu
      mainContainer.add(modelGroup);

     

      renderer = _renderer;
      controls = _controls;
      setSceneState({
        scene,
        camera,
        modelGroup,
        ambientLight,
        directionalLight,
        renderer,
        gridHelper,
        mainContainer // Dodaj mainContainer u sceneState
      });
      homeRef.current = {
  matrix: modelGroup.matrix.clone(),
  autoUpdate: modelGroup.matrixAutoUpdate,
  containerPos: mainContainer.position.clone(),
  containerRot: mainContainer.rotation.clone(),
  containerScale: mainContainer.scale.clone(),
};

      try {
        const response = await fetch("/demo/3dmodels/models.json");
        const models = await response.json();
        const found = models.find((m) => m.Code === modelCode);

        if (!found) {
          console.error("❌ Model nije pronađen u models.json za:", modelCode);
          return;
        }

        const filePath = `/demo/3dmodels/${found.File}`;
  const model = await loadModel(filePath, modelGroup, gltfLoader, reportXHRProgress)
    .catch(error => {
      console.error("Model loading failed:", error);
      // Emit error event to loading manager
      subs.forEach(fn => fn({ type: "error", progress: 0, error }));
      throw error;
    });

  model.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = shadowsEnabled;
      child.receiveShadow = shadowsEnabled;
    }
  });

        const origPos = new Map();
        const explodeVecs = new Map();

        const center = new THREE.Vector3();
        new THREE.Box3().setFromObject(model).getCenter(center);

        model.traverse((child) => {
          if (child.isMesh) {
            const pos = child.position.clone();
            origPos.set(child, pos);
            const dir = new THREE.Vector3().subVectors(pos, center).normalize();
            explodeVecs.set(child, dir);

            child.material.clippingPlanes = [clipPlane];
            child.material.clipIntersection = true;
            child.material.needsUpdate = true;
          }
        });

         model.traverse(child => {
  if (child.isMesh && child.material) {
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach((m, i) => {
      if (!m?.name || !m.name.trim()) {
        m.name = `${child.name || "Mesh"}_Mat${i}`; // fallback
      }
    });
  }
});

// ⬇️ sada JE model u sceni → možemo prikupiti imena i napuniti dropdown
const names = collectSceneMaterialNames(modelGroup);
console.log("🎯 Materijali u sceni:", names);
setElementOptions(names);


        setOriginalPositions(origPos);
        setExplodeVectors(explodeVecs);

        console.log("✅ Model uspešno učitan:", filePath);
      } catch (err) {
        console.error("❌ Greška:", err.message);
      }

      const reticle = new THREE.Mesh(
        new THREE.RingGeometry(0.12, 0.14, 48).rotateX(-Math.PI / 2),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.85 })
      );
      reticle.matrixAutoUpdate = false;
      reticle.visible = false;
      scene.add(reticle);

      let xrSession = null;
      let refSpace = null;
      let viewerSpace = null;
      let hitSource = null;
      let anchor = null;
      let lastFrame = null;

      const controller = renderer.xr.getController(0);
      controller.addEventListener("select", onSelect);
      scene.add(controller);

      renderer.xr.addEventListener("sessionstart", async () => {
        xrSession = renderer.xr.getSession();
        prevAutoRotateRef.current = autoRotateRef.current; // zapamti
        autoRotateRef.current = false;                     // isključi u AR-u
        placedInitiallyRef.current = false;
        try {
          refSpace    = await xrSession.requestReferenceSpace("local-floor");
          viewerSpace = await xrSession.requestReferenceSpace("viewer");
          hitSource   = await xrSession.requestHitTestSource({ space: viewerSpace });

          setArActive(true);
          // Zaključa scroll tokom AR-a
const root = document.documentElement;
  bodyScrollBackupRef.current = {
    overflow: document.body.style.overflow || '',
    touchAction: document.body.style.touchAction || '',
    overscrollBehavior: root.style.overscrollBehavior || ''
  };
  document.body.style.overflow = 'hidden';
  document.body.style.touchAction = 'none';
  root.style.overscrollBehavior = 'none';

          if (controls) controls.enabled = false;

          if (arOverlayRef.current) arOverlayRef.current.style.pointerEvents = "auto";
          modelGroup.visible = true;
        } catch (e) {
          console.error("XR sessionstart error:", e);
          setArActive(false);
        }
      });

      renderer.xr.addEventListener("sessionend", () => {
        restoreScrollAndLayout();
        autoRotateRef.current = prevAutoRotateRef.current; // vrati stanje
        resetModelToHome();   // ⬅️ vrati na grid “home” transform
        hitSource = null;
        anchor = null;
        setArActive(false);
        // Vrati scroll posle AR-a
const { overflow, touchAction, overscrollBehavior } = bodyScrollBackupRef.current || {};
document.body.style.overflow = overflow ?? '';
document.body.style.touchAction = touchAction ?? '';
document.documentElement.style.overscrollBehavior = overscrollBehavior ?? '';
// ako je ostao tvoj custom fullscreen, izađi iz njega
try { if (document.fullscreenElement) document.exitFullscreen(); } catch {}

        if (controls) controls.enabled = true;
        if (arOverlayRef.current) arOverlayRef.current.style.pointerEvents = "none";
        controller.removeEventListener("select", onSelect);
      });

      function alignForWall(quaternion) {
        const qFix = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          -Math.PI / 2
        );
        return quaternion.clone().multiply(qFix);
      }

      async function onSelect() {
        if (!lastFrame || !hitSource) return;
        const results = lastFrame.getHitTestResults(hitSource);
        if (!results.length) return;

        const hit = results[0];
        const pose = hit.getPose(refSpace);
        if (!pose) return;

        anchor = await hit.createAnchor();
        if (!anchor) return;

        const m = new THREE.Matrix4().fromArray(pose.transform.matrix);
        modelGroup.matrixAutoUpdate = false;

        if (placeOn === "wall") {
          const q = new THREE.Quaternion();
          const s = new THREE.Vector3(), p = new THREE.Vector3();
          m.decompose(p, q, s);
          const qWall = alignForWall(q);
          const mw = new THREE.Matrix4().compose(p, qWall, s);
          modelGroup.matrix.copy(mw);
        } else {
          modelGroup.matrix.copy(m);
        }

        modelGroup.visible = true;
        reticle.visible = false;
      }

      _renderer.xr.enabled = true;

      async function startARSession() {
        if (!navigator.xr) {
          alert("WebXR nije podržan na ovom uređaju/pretraživaču.");
          return;
        }

        const sessionInit = {
          requiredFeatures: ["local-floor", "hit-test", "anchors"],
          optionalFeatures: ["dom-overlay"],
          domOverlay: { root: arOverlayRef.current || document.body },
        };

        try {
          const session = await navigator.xr.requestSession("immersive-ar", sessionInit);
          _renderer.xr.setSession(session);
        } catch (e) {
          console.warn("Neuspeo AR start:", e);
        }
      }

      startARRef.current = startARSession;

      // ===== Render petlja (radi i za AR i za "običan" prikaz) =====
      function frameLoop(time, frame) {
        lastFrame = frame;
        
        let needsRender = false;

        // Ako smo u AR-u i još nismo postavili inicijalno a nema ankera:
if (arActive && !anchor && !placedInitiallyRef.current) {
  const viewerPose = frame?.getViewerPose(refSpace);
  if (viewerPose) {
    const tp = viewerPose.views[0].transform;
    // pozicija kamere
    const camPos = new THREE.Vector3(tp.position.x, tp.position.y, tp.position.z);
    // orijentacija kamere
    const qViewer = new THREE.Quaternion(tp.orientation.x, tp.orientation.y, tp.orientation.z, tp.orientation.w);

    // napred (forward) vektor
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(qViewer).normalize();

    // željena pozicija modela ispred korisnika
    const d = initialDistanceRef.current; // 1.6m (podesivo)
    const pos = camPos.clone().add(forward.multiplyScalar(d));

    // ako postavljaš na pod, spusti na pod (local-floor ima Y=0 kao pod)
    if (placeOn === 'floor') pos.y = 0;

    // orijentiši model da "gleda" ka korisniku -> samo yaw iz kamere
    const e = new THREE.Euler().setFromQuaternion(qViewer, 'YXZ');
    const yawOnly = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, e.y, 0));

    // ako postavljaš na zid, primeni tvoj wall fix
    const qFinal = (placeOn === 'wall')
      ? yawOnly.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1,0,0), -Math.PI/2))
      : yawOnly;

    const scale = modelGroup.scale.clone(); // zadrži skalu
    const m = new THREE.Matrix4().compose(pos, qFinal, scale);
    modelGroup.matrixAutoUpdate = false;
    modelGroup.matrix.copy(m);
    modelGroup.visible = true;

    placedInitiallyRef.current = true; // samo jednom uradi ovo
  }
}
        if (frame && hitSource) {
          if (!anchor) {
            const hits = frame.getHitTestResults(hitSource);
            if (hits.length) {
              const pose = hits[0].getPose(refSpace);
              if (pose) {
                reticle.visible = true;
                reticle.matrix.fromArray(pose.transform.matrix);
              }
            } else {
              reticle.visible = false;
            }
          } else {
            reticle.visible = false;
          }

          if (anchor) {
            const aPose = frame.getPose(anchor.anchorSpace, refSpace);
            if (aPose) {
              const m = new THREE.Matrix4().fromArray(aPose.transform.matrix);
              if (placeOn === 'wall') {
                const q = new THREE.Quaternion();
                const s = new THREE.Vector3(), p = new THREE.Vector3();
                m.decompose(p, q, s);
                const qWall = alignForWall(q);
                const mw = new THREE.Matrix4().compose(p, qWall, s);
                modelGroup.matrix.copy(mw);
              } else {
                modelGroup.matrix.copy(m);
              }
            }
          }
        } else {
          controls && controls.update();
        }

        // 🚀 Klackanje modela (ako je uključeno) - Sada rotiramo mainContainer umesto modelGroup
        if (mainContainer && autoRotateRef.current) {
  currentRotation += rotationSpeed;
  if (currentRotation > maxRotation || currentRotation < -maxRotation) {
    rotationSpeed = -rotationSpeed;
  }
  mainContainer.rotation.y = currentRotation;
  needsRender = true;
}

        if (needsRender) {
          renderer.render(scene, camera);
        } else {
          renderer.render(scene, camera);
        }
      }

      renderer.setAnimationLoop(frameLoop);

      document
        .getElementById("backgroundColorPicker")
        ?.addEventListener("input", function () {
          const selectedColor = this.value;
          scene.background = new THREE.Color(selectedColor);
          renderer.render(scene, camera);
        });

      document.getElementById("toggleRotation")?.addEventListener("click", () => {
        autoRotate = !autoRotate;
        renderer.render(scene, camera);
      });

      document.getElementById("toggleGrid")?.addEventListener("click", () => {
        if (gridHelper) gridHelper.visible = !gridHelper.visible;
      });

      document.getElementById("lightColor")?.addEventListener("input", function () {
        const color = new THREE.Color(this.value);
        directionalLight.color.set(color);
        ambientLight.color.set(color);
        renderer.render(scene, camera);
      });

      document.getElementById("ambientLightSlider")?.addEventListener("input", function (e) {
        const intensity = parseFloat(e.target.value);
        ambientLight.intensity = intensity;
        renderer.render(scene, camera);
      });

      document.getElementById("directionalLightSlider")?.addEventListener("input", function (e) {
        const intensity = parseFloat(e.target.value);
        directionalLight.intensity = intensity;
        renderer.render(scene, camera);
      });

      
     

      document.getElementById("exportPNG")?.addEventListener("click", () => {
        const originalBackground = scene.background;
        scene.background = null;
        renderer.render(scene, camera);
        const dataURL = renderer.domElement.toDataURL("image/png");
        scene.background = originalBackground;
        const a = document.createElement("a");
        a.href = dataURL;
        a.download = "scene.png";
        a.click();
      });

      document.getElementById("viewMode")?.addEventListener("change", function () {
        updateViewMode(this.value);
      });
    };

    setup();

    return () => {

      try { renderer?.setAnimationLoop(null); } catch {}
      restoreScrollAndLayout();
  // ukloni sve event listenere koje si dodao preko document.getElementById(...)
  document.getElementById("backgroundColorPicker")?.removeEventListener("input", /* ista fn ref ako je izvučena */);
  document.getElementById("toggleRotation")?.removeEventListener("click", /* ... */);
  document.getElementById("toggleGrid")?.removeEventListener("click", /* ... */);
  document.getElementById("lightColor")?.removeEventListener("input", /* ... */);
  document.getElementById("ambientLightSlider")?.removeEventListener("input", /* ... */);
  document.getElementById("directionalLightSlider")?.removeEventListener("input", /* ... */);
  document.getElementById("azimuthSlider")?.removeEventListener("input", /* ... */);
  document.getElementById("altitudeSlider")?.removeEventListener("input", /* ... */);
  document.getElementById("viewMode")?.removeEventListener("change", /* ... */);

      if (containerRef.current) {
        while (containerRef.current.firstChild) {
          containerRef.current.removeChild(containerRef.current.firstChild);
        }
      }
    };
  // eslint-disable-next-line
}, [modelCode]);

const resetModelToHome = React.useCallback(() => {
  if (!sceneState || !homeRef.current) return;
  const { modelGroup, mainContainer, renderer, scene, camera } = sceneState;
  const h = homeRef.current;

  // dekomponuj “home” matricu u poz/quaternion/scale da radi sa autoUpdate
  const p = new THREE.Vector3();
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3();
  h.matrix.decompose(p, q, s);

  // vrati model na početno
  modelGroup.matrixAutoUpdate = true;
  modelGroup.position.copy(p);
  modelGroup.quaternion.copy(q);
  modelGroup.scale.copy(s);
  modelGroup.updateMatrix();

  // vrati i container (grid + rotacija)
  mainContainer.position.copy(h.containerPos);
  mainContainer.rotation.copy(h.containerRot);
  mainContainer.scale.copy(h.containerScale);

  renderer.render(scene, camera);
}, [sceneState]);


  useEffect(() => {
  if (!sceneState) return;
  const { scene, camera, renderer, ambientLight, directionalLight, modelGroup } = sceneState;

  // 1) Uhvati elemente JEDNOM
  const el = {
    bg: document.getElementById("backgroundColorPicker"),
    toggleRot: document.getElementById("toggleRotation"),
    grid: document.getElementById("toggleGrid"),
    lightColor: document.getElementById("lightColor"),
    amb: document.getElementById("ambientLightSlider"),
    dir: document.getElementById("directionalLightSlider"),
    az: document.getElementById("azimuthSlider"),
    alt: document.getElementById("altitudeSlider"),
    viewMode: document.getElementById("viewMode"),
    exportPNG: document.getElementById("exportPNG"),
  };

  // 2) Definiši STABILNE handler funkcije
  const onBg = (e) => {
    scene.background = new THREE.Color(e.target.value);
    renderer.render(scene, camera);
  };
  const onToggleRot = () => {
    // koristiš promenljivu iz tvog setup-a; ako je u drugom scope-u, stavi je u useRef
    autoRotateRef.current = !autoRotateRef.current;
    // i odmah render:
    renderer.render(scene, camera);
  };
  const onGrid = () => {
    const g = scene.getObjectByName("__gridHelper");
    if (g) g.visible = !g.visible;
    renderer.render(scene, camera);
  };
  const onLightColor = (e) => {
    const c = new THREE.Color(e.target.value);
    ambientLight.color.set(c);
    directionalLight.color.set(c);
    renderer.render(scene, camera);
  };
  const onAmb = (e) => { ambientLight.intensity = parseFloat(e.target.value); renderer.render(scene, camera); };
  const onDir = (e) => { directionalLight.intensity = parseFloat(e.target.value); renderer.render(scene, camera); };

  const onAzAlt = () => {
    if (!el.az || !el.alt) return; // ← sprečava “reading 'value' of null”
    const azimuth  = THREE.MathUtils.degToRad(parseFloat(el.az.value  || "0"));
    const altitude = THREE.MathUtils.degToRad(parseFloat(el.alt.value || "0"));
    const r = 10;
    directionalLight.position.set(
      r * Math.cos(altitude) * Math.sin(azimuth),
      r * Math.sin(altitude),
      r * Math.cos(altitude) * Math.cos(azimuth)
    );
    directionalLight.lookAt(0, 0, 0);
    renderer.render(scene, camera);
  };

  


  const onViewMode = (e) => { 
    // koristi tvoju postojeću updateViewMode
    updateViewMode(e.target.value); 
  };

  const onExport = () => {
    const originalBackground = scene.background;
    scene.background = null;
    renderer.render(scene, camera);
    const dataURL = renderer.domElement.toDataURL("image/png");
    scene.background = originalBackground;
    const a = document.createElement("a");
    a.href = dataURL;
    a.download = "scene.png";
    a.click();
  };

  // 3) Dodaj listenere
  el.bg?.addEventListener("input", onBg);
  el.toggleRot?.addEventListener("click", onToggleRot);
  el.grid?.addEventListener("click", onGrid);
  el.lightColor?.addEventListener("input", onLightColor);
  el.amb?.addEventListener("input", onAmb);
  el.dir?.addEventListener("input", onDir);

  el.viewMode?.addEventListener("change", onViewMode);
  el.exportPNG?.addEventListener("click", onExport);

  // 4) Inicijalna postavka svetla – tek kada slideri postoje
  requestAnimationFrame(onAzAlt);

  // 5) Cleanup sa ISTIM referencama
  return () => {
    el.bg?.removeEventListener("input", onBg);
    el.toggleRot?.removeEventListener("click", onToggleRot);
    el.grid?.removeEventListener("click", onGrid);
    el.lightColor?.removeEventListener("input", onLightColor);
    el.amb?.removeEventListener("input", onAmb);
    el.dir?.removeEventListener("input", onDir);
    el.az?.removeEventListener("input", onAzAlt);
    el.alt?.removeEventListener("input", onAzAlt);
    el.viewMode?.removeEventListener("change", onViewMode);
    el.exportPNG?.removeEventListener("click", onExport);
  };
}, [sceneState]); // ← zakači tek kad scena postoji

const handleStartAR = async () => {
  // ponovo proveri – možda je korisnik promenio pregledač/permisije
  if (!(await detectArSupport())) {
    alert('AR nije podržan u ovom pregledaču/okruženju. Koristi Chrome na Androidu i uključi HTTPS.');
    setArSupported(false);
    return;
  }

  try {
    const sessionInit = {
      requiredFeatures: ['local-floor', 'hit-test', 'anchors'],
      optionalFeatures: ['dom-overlay'],
      domOverlay: { root: arOverlayRef.current || document.body },
    };
    const session = await navigator.xr.requestSession('immersive-ar', sessionInit);
    sceneState?.renderer?.xr?.setSession(session);
  } catch (e) {
    console.warn('AR start failed:', e);
    alert('AR nije dostupan ili je onemogućen (proveri Chrome i Google Play Services for AR).');
    // Pošto je propalo – sakrij dugme nadalje
    setArSupported(false);
  }
};

async function hydrateImagesForAll(materials) {
  const out = [...materials];
  const missing = out.filter(m => !hasDecorImage(m) && m.DecorCode);

  if (!missing.length) return out;

  const CONCURRENCY = 8;
  let idx = 0;

  async function worker() {
    while (idx < missing.length) {
      const m = missing[idx++];
      try {
        const img = await fetchDecorImage(m.DecorCode); // već imaš ovu fn
        if (img) m.imageBase64 = img; // posle ovoga getMaterialImageSrc radi
      } catch {}
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, missing.length) }, worker)
  );

  return out;
}



// 1) Robusna detekcija
const detectArSupport = async () => {
  try {
    // Secure context: https ili localhost
    const secure =
      window.isSecureContext ||
      location.protocol === 'https:' ||
      location.hostname === 'localhost';
    if (!secure) return false;

    if (!('xr' in navigator)) return false;

    // Gating: Android + Chrome (isključi Samsung Internet, Edge, Opera)
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isChrome =
      /Chrome\/\d+/.test(ua) &&
      !/SamsungBrowser/i.test(ua) &&
      !/EdgA\//i.test(ua) &&
      !/OPR\//i.test(ua);
    if (!(isAndroid && isChrome)) return false;

    // WebGL2 potreban
    const gl2 = document.createElement('canvas').getContext('webgl2');
    if (!gl2) return false;

    // Mode podržan?
    const ok = await navigator.xr.isSessionSupported('immersive-ar');
    return !!ok;
  } catch {
    return false;
  }
};

useEffect(() => {
  const rsd = Number(fx?.rates?.RSD);
  if (rsd && quoteApi?.setEuroRate) {
    quoteApi.setEuroRate(rsd);
  }
}, [fx?.rates?.RSD]);

useEffect(() => {
  console.log('[FX] rates:', fx?.rates);
  console.log('[QUOTE] euro_rate:', quoteApi?.quote?.euro_rate);
}, [fx, quoteApi?.quote?.euro_rate]);



// 2) Postavi arSupported na mount-u
useEffect(() => {
  let cancelled = false;
  (async () => {
    const ok = await detectArSupport();
    if (!cancelled) setArSupported(ok);
  })();
  return () => { cancelled = true; };
}, []);

useEffect(() => {
  const mSum = quoteApi.quote.materials.items
    .reduce((s, it) => s + (it.area_m2 > 0 ? it.area_m2 * it.price_per_m2 : 0), 0);

  const kantSum = quoteApi.quote.materials.items
    .reduce((s, it) => s + (it.edge_length_m > 0
        ? it.edge_length_m * (it.edge_price_per_m || 0)
        : 0), 0);

  const eqSum = quoteApi.quote.equipment.items
  .reduce((s, it) => s + it.quantity * (it.net_unit_price ?? it.unit_price ?? 0), 0);

const srvSum = quoteApi.quote.services.items
  .reduce((s, it) => s + it.quantity * (it.net_unit_price ?? it.unit_price ?? 0), 0);

  const calcTotal = round2(mSum + kantSum + eqSum + srvSum);

  dbg("Σ check", {
    euro_rate: quoteApi.quote.euro_rate,
    currency: quoteApi.quote.currency,
    materials_calc: round2(mSum + kantSum),
    materials_reported: quoteApi.quote.materials.total,
    diff_materials: round2((mSum + kantSum) - quoteApi.quote.materials.total),
    equipment_calc: round2(eqSum),
    equipment_reported: quoteApi.quote.equipment.total,
    services_calc: round2(srvSum),
    services_reported: quoteApi.quote.services.total,
    grand_calc: calcTotal,
    grand_reported: quoteApi.quote.grand_total,
    diff_grand: round2(calcTotal - quoteApi.quote.grand_total),
  });
}, [
  quoteApi.quote.euro_rate,
  quoteApi.quote.currency,
  quoteApi.quote.materials.items,
  quoteApi.quote.equipment.items,
  quoteApi.quote.services.items,
  quoteApi.quote.materials.total,
  quoteApi.quote.equipment.total,
  quoteApi.quote.services.total,
  quoteApi.quote.grand_total
]);




  useEffect(() => {
    const controlsPanel = document.getElementById("controls");
    const hideBtn = document.getElementById("hideControlsBtn");
    const showBtn = document.getElementById("showControlsBtn");

    if (!controlsPanel || !hideBtn || !showBtn) return;

    const hide = () => {
      controlsPanel.classList.add("hidden-panel");
      showBtn.style.display = "block";
    };
    const show = () => {
      controlsPanel.classList.remove("hidden-panel");
      showBtn.style.display = "none";
    };

    hideBtn.addEventListener("click", hide);
    showBtn.addEventListener("click", show);

    return () => {
      hideBtn.removeEventListener("click", hide);
      showBtn.removeEventListener("click", show);
    };
  }, [sceneState]);

  const [isLoading, setIsLoading] = useState(true);
const [progress, setProgress] = useState(0);

// Pretplata na globalni manager
useEffect(() => {
  const unsub = subscribeLoading(evt => {
    if (evt.type === "start") {
      setIsLoading(true);
      setProgress(0);
    }
    if (evt.type === "progress" && typeof evt.progress === "number") {
      setProgress(p => Math.max(p, evt.progress));
    }
    if (evt.type === "done") {
      // sakrij posle prvog realnog frame-a
      const r = sceneState?.renderer, s = sceneState?.scene, c = sceneState?.camera;
      if (r && s && c) hideAfterFirstFrame(r, s, c, setIsLoading);
      else setIsLoading(false);
    }
  });
  return unsub;
}, [sceneState]);


   return (
    <div 
      ref={viewerRef} 
      className="relative w-full h-full"
      style={{ zIndex: 1 }}
    >

{/* Full Screen Button – prati stranicu (absolute), a u fullscreen-u je fixed */}
<button
  ref={fullscreenBtnRef}
  onClick={toggleFullscreen}
  onPointerDown={(e) => e.stopPropagation()}
  className={`
    ${isFullscreen ? 'fixed' : 'absolute'}
    left-1/2 -translate-x-1/2 translate-y-[4px]
    z-[80] w-10 h-10 inline-flex items-center justify-center
    rounded-md bg-black/60 text-white hover:bg-black/70
    shadow-md backdrop-blur pointer-events-auto transition
  `}
  style={{
    // FS: poravnaj prema vrhu viewporta + safe-area
    // Non-FS: mali offset unutar viewer kontejnera (bez headerHeight!)
    top: isFullscreen
      ? 'calc(env(safe-area-inset-top, 4px) + 20px)'
      : '21px',
  }}
  aria-label={isFullscreen ? "Izađi iz full screen moda" : "Uđi u full screen mod"}
  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
>
  {isFullscreen ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
    </svg>
  )}
</button>

      <div
        ref={containerRef}
        className="w-full h-full overflow-hidden"
        style={{ margin: 0, padding: 0 }}
      />

    

      {/* SVE KONTROLE - videće se i u fullscreen modu */}
      

      {arSupported && !arActive && (
  <button
    onClick={handleStartAR}
    className="fixed z-[60] px-5 py-2 rounded-full text-white border backdrop-blur-md shadow-lg
               border-white/30 bg-green-600/30 hover:bg-green-600/40 transition pointer-events-auto"
    style={{
      left: '50%',
      transform: 'translateX(-50%)',
      // podigni za visinu headera + malo margine + safe-area na dnu
      bottom: `calc(env(safe-area-inset-bottom, 0px) + ${headerHeight}px + 12px)`,
    }}
    aria-label={t('arOverlay.enable_ar')}
  >
    {t('arOverlay.enable_ar')}
  </button>
)}

     {/* Desni stack: kartica + launcher dugmići */}
<div
  className="
    absolute top-3 inset-x-0
    md:inset-x-auto md:right-2
    z-[70]                   /* iznad headera ako on koristi z-50 */
  "
>
  {/* limiter + sidro za absolute dugmiće */}
  <div className="relative px-4 md:px-0 max-w-[720px] mx-auto md:ml-auto">

    {/* LAUNCH dugmići – uvek iznad kartice */}
    <div className="absolute top-2 left-6 z-[60] pointer-events-auto flex gap-2">
      



    </div>

    {/* Kartica za cene – ispod dugmića */}
    <QuoteWidget
  useQuoteApi={quoteApi}
  fx={fx}
  className="relative z-[50] w-full md:w-72 md:max-w-[18rem]"
  onOpenMaterials={async () => {
  //await ensureWarmupPreload?.();

  let anyCache = [...preloadedByThickRef.current.values()].flat();
  let top = anyCache.slice(0, 80);

  // 🔥 Ako u “top” ima stavki bez slike – hidriri ih odmah da grid ne bude prazan
  const needsHydration = top.some(m => !hasDecorImage(m) && m.DecorCode);
  if (needsHydration) {
    try {
      top = await hydrateImagesForAll(top);
    } catch {}
  }

  setMaterialOptions(prev => prev.length ? prev : top);
  setShowMaterialSelector(true);
}}
  onOpenControls={(section = 'light') => {
    setControlsSection(section);
    setControlsOpen(true);
  }}
/>
  </div>

  {/* Overlay za učitavanje */}
  <div id="loadingOverlay" className="hidden fixed inset-0 z-[101] items-center justify-center bg-black/60">
    <div className="rounded-xl bg-neutral-800 text-white px-4 py-3 shadow-xl">Učitavanje…</div>
  </div>
</div>
      {showMaterialSelector && createPortal(
  <div
    className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 md:p-6"
    role="dialog"
    aria-modal="true"
    onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowMaterialSelector(false); setSelectedStructure(null); } }}
    onKeyDown={(e) => { if (e.key === "Escape") { setShowMaterialSelector(false); setSelectedStructure(null); } }}
    tabIndex={-1}
  >
    <div
      className="
        relative w-full
        md:max-w-6xl
        bg-white dark:bg-gray-900 shadow-2xl
        rounded-xl md:rounded-2xl
        flex flex-col
        h-[92dvh] md:h-[90dvh]
        overflow-hidden
      "
      style={{
        paddingTop: 'max(env(safe-area-inset-top), 0px)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 0px)',
      }}
    >
      {/* HEADER (fixed/sticky within modal) */}
      <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/90 backdrop-blur">
              <div className="flex items-center gap-3 min-w-0">
                <h2 className="text-lg md:text-xl font-bold truncate">{t("decor.header")}</h2>
                {/* Mobile element picker (umesto leve trake) */}
                <div className="md:hidden shrink-0">
                  <select
                    value={selectedElement || ""}
                    onChange={(e) => onExistingMaterialChange(e.target.value)}
                    className="text-sm px-2 py-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"
                    style={{ WebkitAppearance: 'none' }}
                  >
                    <option value="">{t("decor.mobile_select_placeholder")}</option>
                    {elementOptions.map((el) => (
                      <option key={el} value={el}>{el}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={() => { setShowMaterialSelector(false); setSelectedStructure(null); }}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Zatvori"
                title={t("decor.close")}
              >
                <X size={20} />
              </button>
            </div>

            {/* BODY (only this scrolls inside) */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        <aside className="hidden md:block w-64 shrink-0 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
                <h3 className="font-medium mb-3">{t("decor.sidebar_title")}</h3>
                <div className="space-y-1">
                  {elementOptions.map((element) => (
                    <button
                      key={element}
                      onClick={() => onExistingMaterialChange(element)}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition
                        ${selectedElement === element
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                    >
                      <span className="line-clamp-2">{element}</span>
                    </button>
                  ))}
                </div>
              </aside>

              {/* Main */}
              <section className="flex-1 min-w-0 flex flex-col">
          {/* Search bar */}
          <div className="shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-800/90 backdrop-blur">
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder={t("decor.search_placeholder")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    />
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs md:text-sm text-gray-500">
                      {filteredMaterials.length} dekora
                      {filteredMaterials.length > 0 && ` (${filteredMaterials.filter(hasDecorImage).length} sa slikom)`}
                    </span>

                    <div className="flex items-center gap-2">
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="text-sm px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                        style={{ WebkitAppearance: 'none' }}
                      >
                        <option value="price">{t("decor.sort_by_price")}</option>
                        <option value="name">{t("decor.sort_by_name")}</option>
                        <option value="structure">{t("decor.sort_by_structure")}</option>
                      </select>

                      <button
                        onClick={() => { setSearchQuery(''); setSortBy('price'); }}
                        className="p-2 rounded-md text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700/60"
                        title={t("decor.reset_filters")}
                      >
                        <RotateCcw size={16} />
                      </button>

                      {typeof DBG_ON !== 'undefined' && DBG_ON && (
                        <button
                          onClick={() => {
                            console.log('Material options:', materialOptions);
                            console.log('Filtered materials:', filteredMaterials);
                            filteredMaterials.forEach((m, i) => {
                              console.log(`Material ${i}:`, m.DecorCode, 'has image:', !!(m.imageBase64 || m.ImageBase64));
                            });
                          }}
                          className="p-2 rounded-md text-red-500 hover:text-red-700"
                          title="Debug info"
                        >
                          🐛
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Grid LIST scroll */}
          <div className="flex-1 min-h-0 overflow-y-auto p-3 md:p-4">
                  {loadingMaterials ? (
                    <div className="w-full text-center py-10">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-b-transparent border-blue-500" />
                      <p className="mt-3 text-gray-500">{t("decor.loading")}</p>
                    </div>
                  ) : filteredMaterials.length > 0 ? (
                    <div
                      className="
                        grid gap-2 md:gap-3
                        grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5
                      "
                    >
                      {filteredMaterials.map((m) => {
                        const imageUrl = getImageUrl(m);
                        const isSelected =
                          selectedMaterial === m.DecorCode &&
                          (selectedStructure ?? null) === (m.Structure ?? null);

                        return (
                          <button
                            key={`${m.DecorCode}_${m.Structure || 'N/A'}`}
                            onClick={() => {
                              setSelectedMaterial(m.DecorCode);
                              setSelectedStructure(m.Structure || null);
                              setMaterialPreview(imageUrl || "");
                            }}
                            className={`
                              group text-left rounded-lg border
                              border-gray-200 dark:border-gray-700
                              hover:border-blue-400 hover:shadow
                              bg-white dark:bg-gray-800
                              transition p-2 md:p-3
                              ${isSelected ? 'ring-2 ring-blue-500' : ''}
                            `}
                          >
                            <div className="aspect-[4/3] w-full rounded-md bg-gray-100 dark:bg-gray-700 overflow-hidden mb-2">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <ImageOff size={28} />
                                </div>
                              )}
                            </div>

                            <div className="min-w-0">
                              <div className="font-medium text-sm md:text-[15px] leading-snug line-clamp-2">
                                {m.DistributorName || m.DecorCode}
                              </div>
                              <div className="mt-1 text-[11px] md:text-xs text-gray-500 space-x-1">
                                <span className="font-mono">{m.DecorCode}</span>
                                {m.Structure && <span>• {m.Structure}</span>}
                                {(m.Thickness || m.thickness) && (
                                  <span>• {(m.Thickness || m.thickness)}mm</span>
                                )}
                              </div>
                              <div className="mt-2 font-semibold text-blue-600 dark:text-blue-400 text-sm">
                                {m.__priceEUR ? `${m.__priceEUR.toFixed(2)} €/m²` : '—'}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="w-full py-10 flex flex-col items-center gap-4 text-gray-600 dark:text-gray-300">
                      <div className="text-center">
  {materialOptions.length > 0
    ? t("decor.no_results_with_filter")
    : t("decor.no_results_no_materials")}
</div>

                      {/* inline fallback: izbor pune boje */}
                      <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 px-4 py-3 rounded-md border border-gray-200 dark:border-gray-700">
                        <input
                          type="color"
                          value={customColor}
                          onChange={(e) => setCustomColor(e.target.value)}
                          className="h-10 w-10 rounded"
                        aria-label={t("decor.colorpicker_aria")}
                        />
                        <input
                          type="text"
                          value={customColor}
                          onChange={(e) => setCustomColor(e.target.value)}
                          placeholder={t("decor.hex_placeholder")}
                          className="w-32 px-2 py-1 border rounded bg-white dark:bg-gray-700 dark:border-gray-600 font-mono text-sm"
                        />
                        <button
                          onClick={applyCustomColor}
                          disabled={!selectedElement || !hexOk(customColor)}
                          className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
                        >
                          {t("decor.apply_color")}
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={handlePickFromScene}
                        disabled={!selectedElement}
                        className="px-3 py-2 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        {t("decor.pipette")}
                      </button>

                      <p className="text-xs opacity-70">
                        {t("decor.hint_hex", { example: "#e6e6e6" })}
                      </p>
                    </div>
                  )}
                </div>

                {/* Preview & Actions (sticky footer) */}
                <div
                  className="sticky bottom-0 border-t border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/90 backdrop-blur px-4 py-3"
                  style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
                >
                  <div className="flex items-center gap-3">
                    {/* Preview */}
                    {selectedMaterial && materialPreview ? (
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={materialPreview}
                          alt={t("decor.preview_alt")}
                          className="w-14 h-14 md:w-16 md:h-16 object-cover rounded border shadow-sm"
                          draggable="false"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {selectedMaterial}
                            {selectedStructure && <> • {selectedStructure}</>}
                          </div>
                          <div className="text-xs text-gray-500 line-clamp-2">
                            {selectedElement 
  ? t("decor.preview_element_label", { name: selectedElement }) 
  : t("decor.preview_element_missing")
}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs md:text-sm text-gray-500">{t("decor.prompt_select_decor")}</div>
                    )}

                    <div className="flex-1" />

                    <div className="flex gap-2">
                      <button
                        onClick={() => { setShowMaterialSelector(false); setSelectedStructure(null); }}
                        className="px-3 md:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        {t("decor.cancel")}
                      </button>

                      <button
                        onClick={async () => {
                          if (applying) return;
                          setApplying(true);
                          const ok = await applyMaterial();
                          setApplying(false);
                          if (ok) {
                            setShowMaterialSelector(false);
                            setSelectedStructure(null);
                            setMaterialPreview("");
                          }
                        }}
                        disabled={applying || !selectedElement || !selectedMaterial}
                        className="px-3 md:px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                      >
                        {applying ? t("decor.applying") : t("decor.apply_decor")}
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AR Overlay */}
      <div
        ref={arOverlayRef}
        className={`fixed inset-0 ${arActive ? 'block' : 'hidden'} z-[55] pointer-events-none`}
      >
        <div
  className="pointer-events-auto absolute left-1/2 -translate-x-1/2 flex items-center gap-2
             bg-black/50 text-white px-3 py-2 rounded-lg z-[70] top-16 md:top-6"
  style={{ top: 'calc(env(safe-area-inset-top, 0px) + 32px)' }}  // ⬅️ glavni „spusti“
>
  <button
    onClick={() => sceneState?.renderer?.xr?.getSession()?.end()}
    className="px-2 py-1 rounded bg-red-600"
    aria-label="Zatvori AR"
    title={t("arOverlay.close_ar")}
  >
    ✕
  </button>
  <div className="flex gap-2 justify-center">
    <button
      onClick={() => setPlaceOn('floor')}
      className={`px-3 py-1 rounded ${placeOn === 'floor' ? 'bg-green-600 text-white' : 'bg-white/20'}`}
    >
      {t("arOverlay.place_on_floor")}
    </button>
    <button
      onClick={() => setPlaceOn('wall')}
      className={`px-3 py-1 rounded ${placeOn === 'wall' ? 'bg-green-600 text-white' : 'bg-white/20'}`}
    >
      {t("arOverlay.place_on_wall")}
    </button>
  </div>
</div>

      </div>

      

 {/* LOADING OVERLAY - poseban zIndex */}
      <div className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 bg-black/70 transition-opacity ${isLoading ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
  <svg viewBox="0 0 1024 1024" className="w-[min(42vmin,320px)] h-auto">
    <g transform="translate(0,1024) scale(0.1,-0.1)" fill="none" stroke="#d6c176" strokeWidth="40">
      <path className="loader-path" d="M1277 8963 c-4 -3 -7 -857 -7 -1897 1 -2047 4 -2214 54 -2531 174
      -1124 846 -2123 1822 -2708 433 -259 900 -427 1423 -509 192 -30 203 -31 551
      -31 348 0 359 1 551 31 906 143 1689 560 2292 1222 508 557 833 1244 960 2029
      l31 196 4 2103 3 2102 -636 0 -635 0 -1285 -642 -1285 -643 -1285 643 -1285
      642 -633 0 c-349 0 -637 -3 -640 -7z m2558 -645 l1285 -643 1285 643 1285 642
      630 0 631 0 -3 -2097 -4 -2098 -31 -196 c-147 -910 -560 -1687 -1214 -2283
      -556 -507 -1251 -835 -2028 -958 -192 -30 -203 -31 -551 -31 -348 0 -359 1
      -551 31 -651 103 -1227 341 -1738 720 -799 592 -1343 1496 -1497 2487 -50 317
      -53 484 -54 2533 l0 1892 635 0 635 0 1285 -642z"/>
      <path className="loader-path" d="M2550 6438 c0 -771 4 -1313 11 -1428 26 -447 147 -846 368 -1214 102
      -169 212 -309 365 -465 380 -387 829 -626 1377 -733 161 -31 170 -32 449 -32
      279 0 288 1 448 32 549 107 998 346 1378 733 153 156 263 296 365 465 158 264
      265 545 331 871 l32 158 3 1428 c2 785 1 1427 -2 1427 -3 0 -579 -287 -1280
      -637 l-1275 -638 -1275 638 c-701 350 -1280 637 -1285 637 -7 0 -10 -422 -10
      -1242z m1295 595 l1275 -638 1275 638 1276 637 -4 -1422 -3 -1423 -32 -158
      c-111 -550 -348 -996 -728 -1368 -370 -363 -805 -588 -1336 -691 -160 -31
      -169 -32 -448 -32 -279 0 -288 1 -449 32 -546 106 -998 346 -1372 728 -444
      452 -690 1019 -728 1674 -11 184 -16 2660 -6 2660 3 0 579 -287 1280 -637z"/>
    </g>
  </svg>
  <div className="text-[#f3e9b0] font-semibold tracking-wider">
    Loading <span>{progress}%</span>
  </div>
</div>
<PricingDebug quote={quoteApi.quote} multiplier={PRICE_MULTIPLIER} />

{/* Controls modal – uvek montiran, ali se prikazuje kad je open */}
<div
  className={`
    fixed inset-0 z-[96] ${controlsOpen ? 'visible' : 'invisible'}
  `}
  aria-hidden={!controlsOpen}
>
  {/* backdrop */}
  <div
    onClick={closeControls}
    className={`absolute inset-0 bg-black/40 transition-opacity ${controlsOpen ? 'opacity-100' : 'opacity-0'}`}
  />

  {/* sheet */}
  <div
    className={`
      absolute left-1/2 -translate-x-1/2 top-1/3 w-[min(720px,92vw)]
      rounded-2xl shadow-2xl backdrop-blur
      bg-neutral-700/90 text-white
      ring-1 ring-black/10
      transition-transform transition-opacity
      ${controlsOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}
    `}
  >
    {/* header sa tabovima (ikonice) */}
    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
      <div className="flex gap-2">
        <button
          onClick={() => setControlsSection('light')}
          className={`px-3 py-1.5 rounded-md ${controlsSection==='light' ? 'bg-white/20' : 'hover:bg-white/10'}`}
          title="Lighting"
        >💡</button>
        <button
          onClick={() => setControlsSection('look')}
          className={`px-3 py-1.5 rounded-md ${controlsSection==='look' ? 'bg-white/20' : 'hover:bg-white/10'}`}
          title="Appearance"
        >🎨</button>
        <button
          onClick={() => setControlsSection('tools')}
          className={`px-3 py-1.5 rounded-md ${controlsSection==='tools' ? 'bg-white/20' : 'hover:bg-white/10'}`}
          title="Tools"
        >🛠️</button>
      </div>

      <button
        onClick={closeControls}
        className="px-3 py-1.5 rounded-md hover:bg-white/10"
        aria-label="Close"
        title="Close"
      >✕</button>
    </div>

    {/* body – tri sekcije, renderuj jednu po izboru */}
    <div className="p-4 grid gap-4 md:grid-cols-2">
      {controlsSection === 'light' && (
        <>
          <div>
            <label className="block text-sm mb-1">💡 {t('viewer.light_color')}</label>
            <input
  id="lightColor"
  type="color"
  defaultValue="#ffffff"
  className="w-full bg-primary-light h-8 cursor-pointer"
  onInput={(e) => {
    if (!sceneState) return;
    const { ambientLight, directionalLight, renderer, scene, camera } = sceneState;
    const c = new THREE.Color(e.target.value);
    ambientLight.color.set(c);
    directionalLight.color.set(c);
    renderer.render(scene, camera);
  }}
/>
          </div>

          <div>
            <label className="block text-sm mb-1">🌤 {t('viewer.ambient_light')}</label>
            <input
  id="ambientLightSlider"
  type="range" min="0" max="5" step="0.1" defaultValue="1"
  className="w-full"
  onInput={(e) => {
    if (!sceneState) return;
    const { ambientLight, renderer, scene, camera } = sceneState;
    ambientLight.intensity = +e.target.value;
    renderer.render(scene, camera);
  }}
/>
          </div>

          <div>
            <label className="block text-sm mb-1">🔆 {t('viewer.directional_light')}</label>
            <input
  id="directionalLightSlider"
  type="range" min="0" max="10" step="0.1" defaultValue="2"
  className="w-full"
  onInput={(e) => {
    if (!sceneState) return;
    const { directionalLight, renderer, scene, camera } = sceneState;
    directionalLight.intensity = +e.target.value;
    renderer.render(scene, camera);
  }}
/>
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
  <div>
    <label className="block text-sm mb-1">{t('viewer.azimuth')}</label>
    <input
  type="range"
  min="0"
  max="360"
  value={azimuthDeg}
  onChange={(e) => setAzimuthDeg(Number(e.target.value))}
  className="w-full"
/>
  </div>
  <div>
    <label className="block text-sm mb-1">{t('viewer.altitude')}</label>
    <input
  type="range"
  min="-90"
  max="90"
  value={altitudeDeg}
  onChange={(e) => setAltitudeDeg(Number(e.target.value))}
  className="w-full"
/>
  </div>
</div>

        </>
      )}

      {controlsSection === 'look' && (
        <>
          <div>
            <label className="block text-sm mb-1">🎨 {t('viewer.background')}</label>
            <input
  id="backgroundColorPicker"
  type="color" defaultValue="#ffffff"
  className="w-full bg-primary-light h-8 cursor-pointer"
  onInput={(e) => {
    if (!sceneState) return;
    const { scene, renderer, camera } = sceneState;
    scene.background = new THREE.Color(e.target.value);
    renderer.render(scene, camera);
  }}
/>
          </div>
          

          <div className="flex flex-col gap-2">
            <button
  id="toggleRotation"
  className="w-full bg-primary-light text-black rounded py-2 font-semibold"
  onClick={() => { autoRotateRef.current = !autoRotateRef.current; }}
>
  {t("viewer.toggle_rotation")}
</button>
              
            <button
  id="toggleGrid"
  className="w-full bg-primary-light text-black rounded py-2 font-semibold"
  onClick={() => {
    const g = sceneState?.scene?.getObjectByName("gridHelper");
    if (g) g.visible = !g.visible;
  }}
>
  {t("viewer.show_grid")}
</button>
            <button
  id="toggleShadows"
  onClick={toggleShadows}
  className="px-3 py-2 rounded bg-white/10 hover:bg-white/15"
>
  {shadowsEnabled ? t('viewer.hide_shadows') : t('viewer.show_shadows')}
</button>

          </div>

          <div className="md:col-span-2">
            <label className="block text-sm mb-1">{t('viewer.view_mode')}</label>
            <select
  id="viewMode"
  className="w-full bg-primary-light text-black rounded py-2 font-semibold"
  value={currentViewMode}
  onChange={(e) => updateViewMode(e.target.value)}
>
  <option value="realistic">{t("viewer.realistic")}</option>
  <option value="wireframe">{t("viewer.wireframe")}</option>
  <option value="gray">{t("viewer.gray")}</option>
</select>
          </div>
        </>
      )}

      {controlsSection === 'tools' && (
        <>
          <div>
            <button
  id="exportPNG"
  className="w-full bg-primary-light text-black rounded py-2 font-semibold"
  onClick={() => {
    if (!sceneState) return;
    const { scene, renderer, camera } = sceneState;
    const bg = scene.background;
    scene.background = null;
    renderer.render(scene, camera);
    const url = renderer.domElement.toDataURL("image/png");
    scene.background = bg;
    const a = document.createElement("a");
    a.href = url; a.download = "scene.png"; a.click();
  }}
>
  {t("viewer.export_png")}
</button>
          </div>
          <div>
            <button
              onClick={handleBuyClick}
              className="w-full px-3 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
            >
              💳 {t('viewer.buy_download')}
            </button>
          </div>
        </>
      )}
    </div>
  </div>
</div>

    </div>
  );
};

export default Viewer;